// Copyright 2020 Drivetrain Hub LLC
// GraphQL handlers for model data.

import * as THREE from 'three';
import math from "../../assets/js/math";
import gear from "../../assets/js/gear";
import mesh from "../../assets/js/mesh";
import {addArrays, sortByString, subtractArrays, zip} from "../../assets/js/utils";
import {formatCoordStr, getRotationMatrix3, rotateMatrix3} from "../../assets/js/coord";
import {threeGearExternal, threeGearInternal, threeFrameCarrier, threeFrameShaft} from "../../assets/js/three/parts";
import {
  pixiChildFrameFront,
  pixiChildFrameSide,
  pixiGearExternalFront,
  pixiGearExternalSide,
  pixiGearInternalFront,
  pixiGearInternalSide
} from "../../assets/js/pixi/parts";
import icons from "../../config/icons";
import defaults from "../../config/defaults";
import requests from "../../requests";
import handlers from "../../handlers";
import {store} from "../../store";
import {UNITS, mutateUnits} from "./units";
import helper from "./helper";
import lib from "./lib";
import gql from "graphql-tag";

// region FUNCTIONS - PART

function isGround(Id) {
  return Id === requests.model.GROUND_ID;
}

function isPartGear(part) {
  return part.__typename.startsWith('PartGear')
}

function isPartGearDetail(part) {
  return part.__typename === requests.model.TYPENAME.PART.GEAR_DETAIL;
}

function isPartFrameShaft(part) {
  // Checks if a PartFrame is a shaft, excludes ground.
  return part.obj.geom.pitchCircleDiameter === 0 && !isGround(part.Id);
}

function isPartFrameCarrier(part) {
  // Checks if a PartFrame is a carrier.
  return part.obj.geom.pitchCircleDiameter !== 0;
}

function isTopLevelPart(part) {
  // Returns true if part is not mounted to frame and not ground.

  return !part.frame && !isGround(part.Id);
}

// NAMING

function partLabel(part) {
  // Get string to use a part label and alias.

  return part.name ? part.name : part.obj.name;
}

function partIcon(part) {
  // Get string of icon name based on part type.

  switch (part.__typename) {
    case requests.model.TYPENAME.PART.GEAR_DETAIL: {
      const internal = handlers.gear.isInternal(part.obj.geom);
      return !internal ? icons.gearExternal : icons.gearInternal;
    }
    case requests.model.TYPENAME.PART.GEAR_CONCEPT:
      return 'el-icon-warning';
    case requests.model.TYPENAME.PART.GEAR_PHASING:
      return 'el-icon-warning';
    case requests.model.TYPENAME.PART.FRAME:
      return isPartFrameShaft(part) ? icons.partShaft : icons.partCarrier;
  }
}

// PROPS

function posGlobal(part) {
  // Recursively calculate the global position of part.

  if (part.parent) {
    const parent = store.getters.getParent(part);
    return addArrays(posGlobal(parent), part.pos);
  } else {
    return part.pos;
  }
}

function rotGlobal(part) {
  // Recursively calculate the global rotation of part.

  if (part.parent) {
    const parent = store.getters.getParent(part);
    return rotateMatrix3(rotGlobal(parent), part.rot);
  } else {
    return part.rot;
  }
}

// PIXI

function partSize(part) {
  // Get the part width and diameter.  Useful for 2D graphics.
  // All values are positive, including for internal gears.

  let width, diameter;
  const geom = part.obj.geom;

  switch (part.__typename) {
    case requests.model.TYPENAME.PART.GEAR_DETAIL:
      width = geom.facewidth;
      diameter = handlers.gear.isInternal(geom) ? Math.abs(geom.rimDiameter) : geom.tipDiameter;
      break;
    case requests.model.TYPENAME.PART.GEAR_CONCEPT:
      width = geom.facewidth;
      diameter = Math.abs(geom.theoreticalPitchDiameter);
      break;
    case requests.model.TYPENAME.PART.GEAR_PHASING:
      // PLACEHOLDER {phasing} pixi graphics for width and diameter
      break;
    case requests.model.TYPENAME.PART.FRAME:
      width = geom.pitchCircleDiameter * defaults.geom.carrier.aspectRatio;
      diameter = geom.pitchCircleDiameter;
      break;
  }

  return {width, diameter};
}

function pixiGraphicsSide(part) {
  // Draw side-view pixi graphics.

  const geom = part.obj.geom;
  const internal = handlers.gear.isInternal(geom);

  switch (part.__typename) {
    case requests.model.TYPENAME.PART.GEAR_DETAIL:
      if (!internal) {
        return pixiGearExternalSide(geom);
      } else {
        return pixiGearInternalSide(geom);
      }
    case requests.model.TYPENAME.PART.GEAR_CONCEPT:
      // PLACEHOLDER {concept} pixi graphics
      break;
    case requests.model.TYPENAME.PART.GEAR_PHASING:
      // PLACEHOLDER {phasing} pixi graphics
      break;
    case requests.model.TYPENAME.PART.FRAME: {
      return pixiChildFrameSide(geom);
    }
    default:
      throw Error('Could not identify part type.');
  }
}

function pixiGraphicsFront(part) {
  // Draw front-view pixi graphics.

  const geom = part.obj.geom;
  const internal = handlers.gear.isInternal(geom);

  switch (part.__typename) {
    case requests.model.TYPENAME.PART.GEAR_DETAIL:
      if (!internal) {
        return pixiGearExternalFront(geom);
      } else {
        return pixiGearInternalFront(geom);
      }
    case requests.model.TYPENAME.PART.GEAR_CONCEPT:
      // PLACEHOLDER {concept} pixi graphics
      break;
    case requests.model.TYPENAME.PART.GEAR_PHASING:
      // PLACEHOLDER {phasing} pixi graphics
      break;
    case requests.model.TYPENAME.PART.FRAME: {
      return pixiChildFrameFront(geom);
    }
    default:
      throw Error('Could not identify part type.');
  }
}

// THREE

function defaultColor(part) {
  // Default part viewer `color` prop.

  let color;
  if (!part.viewer.color) {
    const isFrame = part.__typename === requests.model.TYPENAME.PART.FRAME;
    color = isFrame ? '#409eff' :  '#95a6bb';
  } else {
    color = part.viewer.color;
  }

  return color;
}

function defaultViewer(part) {
  // Default part viewer props.

  return {
    ...part.viewer,
    color: defaultColor(part),
  }
}

function threeMatrix4(rot) {
  // Create THREE.Matrix4 from 3x3 matrix represented by an array of arrays.

  let m = new THREE.Matrix4();
  const [vx, vy, vz] = rot;
  const mx = [...vx, 0];
  const my = [...vy, 0];
  const mz = [...vz, 0];
  const mw = [0, 0, 0, 1];
  m.set(...[mx, my, mz, mw].flat());
  return m;
}

function threeGeometry(part) {
  // Returns THREE.BufferGeometry object for use with THREE.Mesh in three.js scene.

  switch (part.__typename) {
    case requests.model.TYPENAME.PART.FRAME: {
      // PLACEHOLDER {3D} consider calling partSize here instead of in threeFrame* functions
      const parts = store.getters.getSubframes(part);
      if (isPartFrameShaft(part)) {
        return threeFrameShaft(parts);
      } else {
        return threeFrameCarrier(part.obj.geom, parts);
      }
    }
    case requests.model.TYPENAME.PART.GEAR_DETAIL: {
      const geom = part.obj.geom;
      const root = part.obj.tooth.finishedRoot;
      const internal = handlers.gear.isInternal(geom);

      if (!internal) {
        return threeGearExternal(geom, root);
      } else {
        return threeGearInternal(geom, root);
      }
    }
    case requests.model.TYPENAME.PART.GEAR_CONCEPT:
      // PLACEHOLDER {concept gear} method to create three.js object
      break;
    case requests.model.TYPENAME.PART.GEAR_PHASING:
      // PLACEHOLDER {phasing gear} method to create three.js object
      break;
  }
}

function threeMesh(part) {
  // Assign material, viewer props, and unique identifier to three.js geometry object.
  // Spatial data is handled in the context of the three.js scene due to reference frame relationships.
  // Returns THREE.Mesh object for use in three.js scene.

  const {color, transparent, visible} = defaultViewer(part);

  // define material
  let material = defaults.three.material.clone();
  material.color.set(color);
  material.transparent = transparent;
  material.opacity = transparent ? defaults.three.opacity : 1;

  // define three part; set unique identifier and visibility
  let part3 = new THREE.Mesh(threeGeometry(part), material);
  part3.name = part.Id;
  part3.visible = visible;

  return part3;
}

function threeConn(conn) {
  // Draw crosshair(s) for the nodes representing a connection.
  // Returns THREE.Group with the node object(s).
  // https://threejs.org/docs/index.html#api/en/helpers/ArrowHelper
  // https://discourse.threejs.org/t/always-render-mesh-on-top-of-another/120/3

  // size settings
  const CROSSHAIR_LENGTH = store.state.ui.size.crossHairLength;
  const CROSSHAIR_HALF_LENGTH = CROSSHAIR_LENGTH / 2;

  const crossHair = (part, color=0x0) => {
    // origin of node
    const pos = posGlobal(part);
    const origin = pos.map(p => p - CROSSHAIR_HALF_LENGTH);
    const originX = new THREE.Vector3(origin[0], pos[1], pos[2]);
    const originY = new THREE.Vector3(pos[0], origin[1], pos[2]);
    const originZ = new THREE.Vector3(pos[0], pos[1], origin[2]);

    const dirX = new THREE.Vector3(1, 0, 0);
    const dirY = new THREE.Vector3(0, 1, 0);
    const dirZ = new THREE.Vector3(0, 0, 1);

    const hairX = new THREE.ArrowHelper( dirX, originX, CROSSHAIR_LENGTH, color, 0, 0 );
    const hairY = new THREE.ArrowHelper( dirY, originY, CROSSHAIR_LENGTH, color, 0, 0 );
    const hairZ = new THREE.ArrowHelper( dirZ, originZ, CROSSHAIR_LENGTH, color, 0.2 * CROSSHAIR_LENGTH, 0.2 * CROSSHAIR_LENGTH );

    // overlay the nodes
    const updateMat = (c) => {c.material.depthTest = false};
    hairX.children.forEach(c => updateMat(c));
    hairY.children.forEach(c => updateMat(c));
    hairZ.children.forEach(c => updateMat(c));

    let crosshair = new THREE.Group();
    crosshair.add(hairX);
    crosshair.add(hairY);
    crosshair.add(hairZ);

    return crosshair;
  };

  let color = 0x0;
  switch (conn.typ) {
    case requests.model.ConnectionType.RATIO: {
      // draw a crosshair at each part datum
      // color = 0x545454;  // dark gray
      const node1 = crossHair(conn.parts[0], color);
      const node2 = crossHair(conn.parts[1], color);
      return new THREE.Group().add(node1, node2);
    }
    case requests.model.ConnectionType.CLUTCH:
      // color = 0xf542dd;  // pink
      break;
    case requests.model.ConnectionType.REVOLUTE:
      // color = 0xf03232;  // red
      break;
    case requests.model.ConnectionType.WELD:
      // color = 0x0;  // black
      break;
  }

  // draw crosshair at part2 datum
  return crossHair(conn.parts[1], color);
}

// EXPORT / REPORT

function idPart(part) {
  // Define `id` of HTML element to use with href.
  return `part-${part.Id}`;
}

function hrefPart(part) {
  return `#part-${part.Id}`;
}

function tableAssm(part) {
  // Return array of part assembly data for tabular display.

  // handle case of undefined or null part
  if (!part) {
    part = {
      frame: null,
      parent: null,
      pos: requests.model.ORIGIN,
      rot: requests.model.EYE,
    }
  }

  // define frame value and href
  const frame = store.getters.getFrame(part);
  let frameHref, frameValue = '-';
  if (frame) {
    frameValue = frame.name;
    frameHref = hrefPart(frame);
  }

  // define parent value and href
  const parent = store.getters.getParent(part);
  let parentHref, parentValue = '-';
  if (frame) {
    parentValue = parent.name;
    parentHref = hrefPart(parent);
  }

  // define subframes
  const subframes = store.getters.getSubframes(part);
  const subassembledItems = subframes.map(p => {
    return {
      parameter: `Subassembled part`,
      symbol: '',
      value: partLabel(p),
      units: UNITS['none'],
      href: hrefPart(p),
    }
  });

  return [
    {
      parameter: 'Assembled to',
      symbol: '',
      value: frameValue,
      units: UNITS['none'],
      href: frameHref,
    }, {
      parameter: 'Relative to',
      symbol: '',
      value: parentValue,
      units: UNITS['none'],
      href: parentHref,
    }, {
      parameter: 'Position, local',
      symbol: '$\\vec{r}$',
      value: formatCoordStr(part.pos),
      units: UNITS['length']
    }, {
      parameter: 'Position, global',
      symbol: '$\\vec{R}$',
      value: formatCoordStr(posGlobal(part)),
      units: UNITS['length']
    }, {
      parameter: 'Axis of rotation, local',
      symbol: '$\\hat{u}_z$',
      value: formatCoordStr(part.rot[2]),
      units: UNITS['none']
    }, {
      parameter: 'Axis of rotation, global',
      symbol: '$\\hat{U}_z$',
      value: formatCoordStr(rotGlobal(part)[2]),
      units: UNITS['none']
    },
    ...subassembledItems
  ];
}

function tableConns(part) {
  // Return array of part connection data for tabular display.
  // NOTE: Tried to use html string for icon as `symbol`, but did not work.

  // define connections
  const conns = store.getters.getPartConnections(part);
  const table = conns.map(c => {
    const connectingPart = c.parts.find(p => p.Id !== part.Id);
    const ground = isGround(connectingPart.Id);
    return {
      parameter: connTypeLabel(c),
      value: partLabel(connectingPart),
      units: UNITS['none'],
      href: !ground ? hrefPart(connectingPart) : '',
    }
  });
  return sortByString(table, 'parameter')
}

// region GEAR

function gearFilename(part, prefix='', suffix='') {
  const gear = part.obj;
  const naming = !part.name ? gear.name : `${gear.name} - ${part.name}`;
  const fullname = !gear.number ? naming : `${gear.number}_${naming}`;
  return prefix + fullname + suffix;
}

function getExternalCylindricalGearInput(part) {
  // part arg must include tooth data with root params.
  // Returns data for GraphQL ExternalCylindricalGearInput type.

  const geom = part.obj.geom;
  const root = part.obj.tooth.finishedRoot;

  // get protuberance pressure angle
  let protuberancePressureAngleNormal;
  if (geom.cutType === requests.gear.CutType.GEN_RACK) {
    protuberancePressureAngleNormal = geom.cutGeneratingRack.protuberanceAngle;
  }

  // check for undercut
  let pressureAngleNormal = geom.pressureAngleNormal.valueOf();
  if (geom.unitAngle === 'deg') pressureAngleNormal = math.deg2rad(pressureAngleNormal);

  let delta;
  const hasProtuberanceInvolute = root.protuberanceBoundaryDiameter !== root.filletBoundaryDiameter;

  if (hasProtuberanceInvolute) {
    // protuberance involute; check for undercut between root fillet and protuberance involute
    // only possible for rack generated gears; pinion cutters do not consider a protuberance involute
    delta = root.filletBoundaryCutAngle - protuberancePressureAngleNormal;
  } else {
    // no protuberance involute; check for undercut between root fillet and involute profile
    delta = root.filletBoundaryCutAngle - pressureAngleNormal;
  }
  const undercut = delta > 1e-6;

  // DEV ONLY
  // console.log('protuberancePressureAngleNormal', protuberancePressureAngleNormal * 180 / Math.PI);
  // console.log('root.filletBoundaryCutAngle', root.filletBoundaryCutAngle * 180 / Math.PI);
  // console.log('undercut', undercut);
  // console.log('delta (rad)', delta);
  // console.log('delta (deg)', delta * 180 / Math.PI);
  // console.log('geom.unitAngle',geom.unitAngle);
  // console.log('geom.pressureAngleNormal', geom.pressureAngleNormal);

  const gear = {
    moduleNormal: geom.moduleNormal,
    numberOfTeeth: geom.numberOfTeeth,
    pressureAngleNormal: geom.pressureAngleNormal,
    helixAngle: geom.helixAngle,
    facewidth: geom.facewidth,
    tipDiameter: geom.tipDiameter,
    rootProfile: zip(root.x, root.y),  // list of coordinates, [[x0, y0], ...]
    protuberancePressureAngleNormal: hasProtuberanceInvolute ? protuberancePressureAngleNormal : null,
    protuberanceBoundaryDiameter: hasProtuberanceInvolute ? root.protuberanceBoundaryDiameter : null,
    boreDiameter: geom.rimDiameter,
  };

  return {gear, undercut}
}

function getInternalCylindricalGearInput(part) {
  // part arg must include tooth data with root params.
  // Returns data for GraphQL InternalCylindricalGearInput type.

  const geom = part.obj.geom;
  const root = part.obj.tooth.finishedRoot;

  return {
    moduleNormal: geom.moduleNormal,
    numberOfTeeth: geom.numberOfTeeth,
    pressureAngleNormal: geom.pressureAngleNormal,
    helixAngle: geom.helixAngle,
    facewidth: geom.facewidth,
    tipDiameter: geom.tipDiameter,
    rimDiameter: geom.rimDiameter,
    rootProfile: zip(root.x, root.y),  // list of coordinates, [[x0, y0], ...]
  }
}

function getGearMicroInput(part) {
  // part arg must include gear micro params.
  // Returns data for GraphQL GearMicroInput type.

  const micro = part.obj.micro;

  let profileRight = {...micro.profileRight};
  delete profileRight.__typename;

  let leadRight = {...micro.leadRight};
  delete leadRight.__typename;

  let profileLeft = {...micro.profileLeft};
  delete profileLeft.__typename;

  let leadLeft = {...micro.leadLeft};
  delete leadLeft.__typename;

  return {profileRight, leadRight, profileLeft, leadLeft};
}

// endregion

// endregion

// region FUNCTIONS - PARTS

function posRelative(part1, part2) {
  // Compute position of part1 relative to part2.

  const pos1 = posGlobal(part1);
  const pos2 = posGlobal(part2);
  return subtractArrays(pos1, pos2);
}

function rotRelative(part1, part2) {
  // Compute orientation of part1 relative to part2.

  const rotGlobal1 = rotGlobal(part1);
  const rotGlobal2 = rotGlobal(part2);
  return getRotationMatrix3(rotGlobal1, rotGlobal2);
}

function isMeshing(part1, part2) {
  // Check if two parts are constrained by a RATIO connection.

  const partsConnectedByRatio = store.getters.getConnectedParts(part1, requests.model.ConnectionType.RATIO);
  return !!partsConnectedByRatio.find(p => p.Id === part2.Id);
}

function isMeshable(part1, part2) {
  // Check if two gear parts are compatible to mesh.

  const geom1 = part1.obj.geom;
  const geom2 = part2.obj.geom;

  // check module and pressure angle
  if (geom1.moduleNormal !== geom2.moduleNormal) return false;
  if (geom1.pressureAngleNormal !== geom2.pressureAngleNormal) return false;

  // check internal vs external type
  const isInternal1 = handlers.gear.isInternal(geom1);
  const isInternal2 = handlers.gear.isInternal(geom2);
  const compatibleType = ![isInternal1, isInternal2].every(Boolean);
  if (!compatibleType) return false;

  let compatibleHelix = false;
  if (!isInternal1 && !isInternal2) {
    compatibleHelix = geom1.helixAngle === -geom2.helixAngle;
  } else if (compatibleType) {
    compatibleHelix = geom1.helixAngle === geom2.helixAngle;
  }

  return compatibleHelix;
}

function centerDistanceSpatial(part1, part2) {
  const [dX, dY] = posRelative(part1, part2);
  return Math.hypot(dX, dY);
}

function centerDistanceWorking(part1, part2) {
  return handlers.gear.internalMultiplier(part1.obj, part2.obj) * centerDistanceSpatial(part1, part2);
}

function meshDataBasic(part1, part2) {
  // Quick calculation of a few mesh parameters for interactive display.
  // Input args must contain full part data structure.

  const geom1 = part1.obj.geom;
  const geom2 = part2.obj.geom;

  // gear ratio
  const gearRatio = handlers.gear.gearRatio(part1.obj, part2.obj);

  // gear params
  const z1 = geom1.numberOfTeeth;
  const z2 = geom2.numberOfTeeth;
  const x1 = gear.profileShiftCoefficient2(geom1);
  const x2 = gear.profileShiftCoefficient2(geom2);
  const moduleTransverse = gear.moduleTransverse1(geom1);
  const pressureAngleTransverse = gear.pressureAngleTransverse1(geom1);

  // center distance
  const referenceCenterDistance = mesh.centerDistanceReference2({moduleTransverse, z1, z2});
  const workingPressureAngleTransverseTheoretical = mesh.workingPressureAngleTransverseTheoretical1({
    x1, x2, z1, z2,
    alphaN: geom1.pressureAngleNormal,
    alphaT: pressureAngleTransverse
  });
  const theoreticalCenterDistance = mesh.centerDistanceTheoretical1({
    workingPressureAngleTransverse: workingPressureAngleTransverseTheoretical,
    referenceCenterDistance,
    pressureAngleTransverse,
  });
  const workingCenterDistance = centerDistanceWorking(part1, part2);

  // working pressure angle
  const dp1 = gear.theoreticalPitchDiameter1({numberOfTeeth: z1, moduleTransverse});
  const dp2 = gear.theoreticalPitchDiameter1({numberOfTeeth: z2, moduleTransverse});
  const db1 = gear.baseDiameter1({theoreticalPitchDiameter: dp1, pressureAngleTransverse});
  const db2 = gear.baseDiameter1({theoreticalPitchDiameter: dp2, pressureAngleTransverse});
  const workingPressureAngleTransverse = mesh.workingPressureAngleTransverse1(workingCenterDistance, db1, db2);

  // backlash
  const backlashRadial = mesh.backlashRadial1(workingCenterDistance, theoreticalCenterDistance);
  const backlashCircumferential = mesh.backlashCircumferential1(backlashRadial, workingPressureAngleTransverse);
  const backlashProfile = mesh.backlashProfile1(backlashCircumferential, workingPressureAngleTransverse);

  return {
    gearRatio,
    referenceCenterDistance,
    theoreticalCenterDistance,
    workingCenterDistance,
    backlashRadial,
    backlashCircumferential,
    backlashProfile
  }
}

function meshPointAnglesGlobal(part1, part2) {
  // Calculate the the global mesh point angle of each part.
  // Mesh point angle computed from the global X-axis, or non-rotated local x-axis, to the point of mesh.
  // Returns array of global mesh point angles, [phi1, phi2].

  // detect internal gearing
  const isInternal1 = handlers.gear.isInternal(part1.obj.geom);
  const isInternal2 = handlers.gear.isInternal(part2.obj.geom);

  // direction of mesh point vectors; internal gearing requires logic
  const vec1To2 = posRelative(part2, part1);
  const vec2To1 = vec1To2.map(el => -el);

  let dirMeshPt1, dirMeshPt2;
  if (isInternal1) {
    // ring > planet
    dirMeshPt1 = vec1To2;
    dirMeshPt2 = dirMeshPt1;
  } else if (isInternal2) {
    // planet > ring
    dirMeshPt1 = vec2To1;
    dirMeshPt2 = dirMeshPt1;
  } else {
    // external gearing
    dirMeshPt1 = vec1To2;
    dirMeshPt2 = vec2To1;
  }

  // mesh point angles relative to global X-axis, or non-rotated local x-axis
  const phiGlobal1 = Math.atan2(dirMeshPt1[1], dirMeshPt1[0]);
  const phiGlobal2 = Math.atan2(dirMeshPt2[1], dirMeshPt2[0]);

  return [phiGlobal1, phiGlobal2];
}

// endregion

// region FUNCTIONS - CONNECTIONS

const CONN_TYPE_LABEL = {
  // typ: label
  CLUTCH: 'Clutch',
  RATIO: 'Gear Mesh',
  REVOLUTE: 'Revolute',
  WELD: 'Weld',
  POWERLOAD: 'Powerload',
};

const CONN_ICON = {
  // typ: icon
  CLUTCH: icons.connectClutch,
  RATIO: icons.connectRatio,
  REVOLUTE: icons.connectRevolute,
  WELD: icons.connectWeld,
  POWERLOAD: icons.powerload,
};

function getConnLabel(conn) {
  // Get string to use a connection label.
  // Apollo cache used to get part data.

  const fragment = gql`
    fragment names on Part {
      name
      obj {
        name
      }
    }`;

  const parts = conn.parts.map(p => store.getters.getPart(p.Id, fragment));
  return connLabel({...conn, parts});
}

function connLabel(conn) {
  // Get string to use a connection label.
  // conn must include part name data, otherwise see getConnLabel.

  if (conn.typ === requests.model.ConnectionType.POWERLOAD) {
    return conn.parts[1].name;
  } else {
    return conn.parts[0].name + ' \u2022 ' + conn.parts[1].name;
  }
}

function connIcon(conn) {
  // Get icon string name based on connection type.

  return CONN_ICON[conn.typ];
}

function connTypeLabel(conn) {
  return CONN_TYPE_LABEL[conn.typ];
}

// endregion

// region FUNCTIONS - MODEL

function modelFilename(model) {
  let modelName = model.name;

  if (model.number) {
    modelName = `${model.number}_${modelName}`;
  }

  if (model.version) {
    modelName += `_${model.version}`;
  }

  return modelName;
}

// endregion

// region ANALYSIS - LOAD CASE

const LOAD_CASE_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  config: ['Configuration', '', 'none'],
  duration: ['Duration', '$t$', 'time'],
  // POWERLOADS / POWERFLOW
  speed: ['Speed', '$\\omega$', 'angularVelocity'],
  torque: ['Torque', '$\\tau$', 'torque'],
  power: ['Power', '$P$', 'power'],
};
const LOAD_CASE_KEYS = Object.keys(LOAD_CASE_INFO);

function lookupLoadCaseInfo(paramStr) {
  return helper.lookupParamInfo(LOAD_CASE_INFO, LOAD_CASE_KEYS, paramStr);
}

/**
 * Convert units of load case properties, including powerloads.
 * @param {Object} loadCase - Load case object.  Function mutates this object.
 * @param {Object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {Object} toUnits - Units to convert to.  Defaults to unit system default.
 * @return {Object} Mutated input object.
 */
function convertLoadCase(loadCase, fromUnits, toUnits) {
  mutateUnits(loadCase, LOAD_CASE_INFO, fromUnits, toUnits, true);
  return loadCase;
}

/**
 * Create an object for tabulating load case data, excluding any results.
 * @param {Object} loadCase - Load case object.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tableLoadCase(loadCase, units) {
  return lib.libTableParams(loadCase, lookupLoadCaseInfo, units)
}

// endregion

// region ANALYSIS - POWERFLOW

const POWERFLOW_POINT_NFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  speed: ['Speed', '$\\omega$', 'angularVelocity'],
  torque: ['Torque', '$\\tau$', 'torque'],
  power: ['Power', '$P$', 'power'],
};

/**
 * Convert units of powerflow *point* results.
 * @param {Object} powerflowPoint - Powerflow point results object.  Function mutates this object.
 * @param {Object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {Object} toUnits - Units to convert to.  Defaults to unit system default.
 * @return {Object} Mutated input object.
 */
function convertPowerflowPoint(powerflowPoint, fromUnits, toUnits) {
  return mutateUnits(powerflowPoint, POWERFLOW_POINT_NFO, fromUnits, toUnits, true);
}

const POWERFLOW_MESH_NFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  cycles: ['Cycles', '-', 'none'],
  frequency: ['Frequency', '$f_m$', 'frequency'],
  pitchLineVelocity: ['Pitch line velocity', '$v_t$', 'linearVelocity'],
  forceAxial: ['Force, axial', '$F_a$', 'force'],
  forceRadial: ['Force, radial', '$F_r$', 'force'],
  forceTangential: ['Force, tangential', '$F_t$', 'force'],
  forceNormal: ['Force, normal', '$F_n$', 'force'],
  forceLoa: ['Force, LoA', '$F_\\text{loa}$', 'force'],
};

/**
 * Convert units of powerflow *mesh* results.
 * @param {Object} powerflowMesh - Powerflow mesh results object.  Function mutates this object.
 * @param {Object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {Object} toUnits - Units to convert to.  Defaults to unit system default.
 * @return {Object} Mutated input object.
 */
function convertPowerflowMesh(powerflowMesh, fromUnits, toUnits) {
  return mutateUnits(powerflowMesh, POWERFLOW_MESH_NFO, fromUnits, toUnits, true);
}

/**
 * Map powerflow results to a data structure for quick lookup of results by part or connection.
 * @param resultsPowerflow {{
 *  points: [{part, connection, speed, torque, cycles}],
 *  meshes: [{connection, cycles, frequency, pitchLineVelocity, forceAxial, forceRadial, forceTangential, forceNormal, forceLoa}]
 *  }}
 * @return {{
 *  points: {string: [{connection, speed, torque}]},
 *  meshes: {string: {cycles, , frequency, pitchLineVelocity, forceAxial, forceRadial, forceTangential, forceNormal, forceLoa}}
 *  }} Powerflow results with part and connection lookup data structure.
 */
function mapResultsPowerflow(resultsPowerflow) {
  let powerflow = {
    points: {},
    meshes: {},
  };

  // restructure points, using part as key
  resultsPowerflow.points.forEach(({part, ...rest}) => {
    if (powerflow.points[part]) {
      powerflow.points[part].push(rest);
    } else {
      powerflow.points[part] = [rest];
    }
  });

  // restructure meshes, using connection as key
  resultsPowerflow.meshes.forEach(({connection, ...rest}) => {
    powerflow.meshes[connection] = rest;
  });

  return powerflow;
}

/**
 *
 * @param partResults {[{connection: string, speed: number, torque: number}]} -
 */
function partTorque(partResults) {
  // partResults is expected to be in the format specified by SET_LOAD_CASE Vuex mutation.
  // All parts are required to achieve static equilibrium, i.e. sum of torques must equal zero.
  // Torques of a carrier are handled differently since some torques may be induced by planet forces.

  // DEV WARNING: What if a carrier only has planets and no other connection points, including powerloads?
  //  ~ Is there a way to compute torque from the planets only?

  let torquePositive = 0;
  let torqueNegative = 0;

  // sum all positive and negative torques of a part
  partResults.forEach(result => {
    if (result.torque > 0) {
      torquePositive += result.torque;
    } else {
      torqueNegative += result.torque;
    }
  });

  return Math.max(torquePositive, Math.abs(torqueNegative));
}

// endregion

export default {
  // PART - CORE
  isPartGear: isPartGear,
  isPartGearDetail: isPartGearDetail,
  isPartFrameShaft: isPartFrameShaft,
  isPartFrameCarrier: isPartFrameCarrier,
  isGround: isGround,
  isTopLevelPart: isTopLevelPart,
  partLabel: partLabel,
  partIcon: partIcon,
  defaultColor: defaultColor,
  defaultViewer: defaultViewer,
  posGlobal: posGlobal,
  rotGlobal: rotGlobal,
  // PART - 2D
  partSize: partSize,
  pixiGraphicsSide: pixiGraphicsSide,
  pixiGraphicsFront: pixiGraphicsFront,
  // PART - 3D
  threeMatrix4: threeMatrix4,
  threeGeometry: threeGeometry,
  threeMesh: threeMesh,
  threeConn: threeConn,
  // PART - EXPORTS
  idPart: idPart,
  hrefPart: hrefPart,
  tableAssm: tableAssm,
  tableConns: tableConns,
  gearFilename: gearFilename,
  getExternalCylindricalGearInput: getExternalCylindricalGearInput,
  getInternalCylindricalGearInput: getInternalCylindricalGearInput,
  getGearMicroInput: getGearMicroInput,
  // PARTS
  posRelative: posRelative,
  rotRelative: rotRelative,
  isMeshing: isMeshing,
  isMeshable: isMeshable,
  meshDataBasic: meshDataBasic,
  meshPointAnglesGlobal: meshPointAnglesGlobal,
  // CONNECTIONS
  CONN_TYPE_LABEL: CONN_TYPE_LABEL,
  CONN_ICON: CONN_ICON,
  getConnLabel: getConnLabel,
  connLabel: connLabel,
  connIcon: connIcon,
  connTypeLabel: connTypeLabel,
  // MODEL
  modelFilename: modelFilename,
  // LOAD CASE
  tableLoadCase: tableLoadCase,
  convertLoadCase: convertLoadCase,
  // POWERFLOW
  convertPowerflowPoint: convertPowerflowPoint,
  convertPowerflowMesh: convertPowerflowMesh,
  mapResultsPowerflow: mapResultsPowerflow,
  partTorque: partTorque,
}
